import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["breakpoints", "children", "layout", "id", "onRequestClose", "spacing", "width"];
import React from 'react';
import styled from 'styled-components';
import FocusLock from 'react-focus-lock';
import { getGlobalThemeValue } from 'utils/ui';
import Box from 'components/Box';
import Portal from 'components/Portal';
import CenterContent from 'components/CenterContent';
import useUniqueId from 'hooks/useUniqueId';
import usePrevious from 'hooks/usePrevious';
import { useModalManager } from './ModalManager';
import { fadeIn } from 'styles/animations';
var ModalWrapper = styled.div.withConfig({
  displayName: "Modal__ModalWrapper",
  componentId: "sc-d7zgp2-0"
})(["position:absolute;top:0;left:0;width:100%;height:100%;height:100dvh;z-index:", ";overflow:auto;overscroll-behavior:contain;"], function (p) {
  return p.theme.globals.z.modals;
});
var ContentWrapper = styled.div.withConfig({
  displayName: "Modal__ContentWrapper",
  componentId: "sc-d7zgp2-1"
})(["position:relative;height:100%;"]);
var Overlay = styled.div.withConfig({
  displayName: "Modal__Overlay",
  componentId: "sc-d7zgp2-2"
})(["position:absolute;top:0;left:0;width:100%;height:100%;background-color:rgba(255,255,255,0.8);backdrop-filter:blur(0.5rem);&[data-should-fade-in='true']{animation:", " 0.3s ease forwards;}"], fadeIn);
function CenterOrFullscreen(_ref) {
  var breakpoints = _ref.breakpoints,
    children = _ref.children,
    layout = _ref.layout,
    spacing = _ref.spacing,
    width = _ref.width;
  if (layout === 'fullscreen') {
    return /*#__PURE__*/React.createElement(Box, {
      height: "100%",
      overflow: "hidden",
      p: spacing,
      breakpoints: breakpoints
    }, children);
  }
  return /*#__PURE__*/React.createElement(CenterContent, {
    fillParent: true,
    p: spacing,
    contentWidth: getGlobalThemeValue('modalWidths', width),
    breakpoints: breakpoints,
    axis: layout === 'top-aligned' ? 'x' : 'both'
  }, children);
}
function Modal(_ref2) {
  var breakpoints = _ref2.breakpoints,
    children = _ref2.children,
    layout = _ref2.layout,
    id = _ref2.id,
    onRequestClose = _ref2.onRequestClose,
    spacing = _ref2.spacing,
    _ref2$width = _ref2.width,
    width = _ref2$width === void 0 ? 'default' : _ref2$width,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var uniqueId = useUniqueId(id);
  var _useModalManager = useModalManager({
      name: uniqueId,
      onRequestClose: onRequestClose
    }),
    modalRef = _useModalManager.modalRef,
    isAtTop = _useModalManager.isAtTop,
    modalCount = _useModalManager.modalCount;
  var previousModalCount = usePrevious(modalCount);
  var isFirstModalOpened = previousModalCount === 0 && modalCount === 1;
  return /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(ModalWrapper, _extends({
    ref: modalRef,
    id: uniqueId
  }, otherProps, {
    role: "dialog"
    // We don't need the aria-modal property here,
    // because we're "polyfilling" its effect (hiding
    // background content) using the 'aria-hidden' library,
    // which also gives us more control over it and allows
    // us to exclude certain elements from being hidden.
    // This solves issue https://github.com/5app/hub/issues/10949
    // aria-modal="true"
  }), /*#__PURE__*/React.createElement(CenterOrFullscreen, {
    layout: layout,
    spacing: spacing,
    width: width,
    breakpoints: breakpoints
  }, isAtTop && /*#__PURE__*/React.createElement(Overlay, {
    "data-should-fade-in": isFirstModalOpened,
    onClick: onRequestClose
  }), /*#__PURE__*/React.createElement(FocusLock, {
    as: ContentWrapper
  }, children))));
}
export default Modal;